import { useQuery } from "@apollo/client";
import moment from "moment";
import { useEffect } from "react";
import {
  EventStatus,
  GetCalendarMeetingsQuery,
  GetCalendarMeetingsQueryVariables,
} from "types/graphql-schema";

import useUiPreferenceCache, {
  CalendarViewEnum,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar } from "@cache/cache";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { meetingStatus } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import Week from "./components/week/week";
import getCalendarMeetings from "./graphql/get-calendar-meetings-query";

const ignored = "ignored";

export const topPixelMargin = 30;
export const minimumEventHeight = 28;
export const hourInPixels = 75;
export const minuteInPixels = hourInPixels / 60;

const Calendar = ({
  selectedDay,
  onClickMeetingLink,
}: {
  selectedDay: string;
  onClickMeetingLink: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const selectedUser = currentUserVar();

  // HOOKS
  useDocumentTitle("Calendar");
  const currentUser = currentUserVar();
  const { uiPreferenceCache } = useUiPreferenceCache();

  const firstDayOfMonth = moment(selectedDay).startOf("month").format();
  const lastDayOfMonth = moment(selectedDay).endOf("month").format();
  const firstDayOfWeek = moment(selectedDay).startOf("isoWeek").format();
  const lastDayOfWeek = moment(selectedDay).endOf("isoWeek").format();
  const startDatetimeGte =
    uiPreferenceCache.calendarView === CalendarViewEnum.month
      ? firstDayOfMonth
      : firstDayOfWeek;
  const startDatetimeLte =
    uiPreferenceCache.calendarView === CalendarViewEnum.month
      ? lastDayOfMonth
      : lastDayOfWeek;

  const calendarFilters = uiPreferenceCache.calendarFilters;

  const meetingFilters = [
    {
      value: meetingStatus.cancelled,
      label: "Show deleted meetings",
    },
    {
      value: ignored,
      label: "Show hidden meetings",
    },
  ];

  if (selectedUser && selectedUser.id !== currentUser.id) {
    meetingFilters.push({
      value: "shared",
      label: "Only shared meetings",
    });
  }

  const { data, refetch, fetchMore } = useQuery<
    GetCalendarMeetingsQuery,
    GetCalendarMeetingsQueryVariables
  >(getCalendarMeetings, {
    variables: {
      forUserId: selectedUser ? selectedUser.id : currentUser.id,
      startDatetime_Gte: startDatetimeGte,
      startDatetime_Lte: startDatetimeLte,
      ignored: calendarFilters.includes(ignored) ? null : false,
      statusIn: calendarFilters.includes(meetingStatus.cancelled)
        ? [EventStatus.Confirmed, EventStatus.Cancelled]
        : [EventStatus.Confirmed],
      participants: calendarFilters.includes("shared")
        ? [currentUser.id]
        : null,
    },
  });

  // fetch for new month in calendar
  useEffect(() => {
    refetch();
  }, [firstDayOfMonth, lastDayOfMonth, refetch]);

  // fetch more if data has multiple pages
  useEffect(() => {
    if (
      data &&
      data.calendar &&
      data.calendar.totalCount > data.calendar.edges.length
    ) {
      fetchMore({
        variables: {
          startDatetime_Gte: startDatetimeGte,
          endDatetime_Lte: startDatetimeLte,
          after: data.calendar.pageInfo.endCursor,
          merge: true,
        },
      });
    }
  }, [data]);

  const meetings = data?.calendar ? assertEdgesNonNull(data.calendar) : [];
  const showSharedEventsBanner =
    selectedUser &&
    selectedUser.id !== currentUser.id &&
    calendarFilters.includes("shared");

  // RENDER
  return (
    <div
      aria-label="Calendar container"
      className={classNames(
        "@container/calendar fs-unmask",
        "flex h-full flex-col bg-red-200",
      )}
    >
      <div className={classNames("flex h-full flex-col bg-white")}>
        <Week
          meetings={meetings}
          selectedDay={selectedDay}
          showSharedEventsBanner={showSharedEventsBanner}
          onClickMeetingLink={onClickMeetingLink}
        />
      </div>
    </div>
  );
};

export default Calendar;
